import { useEffect, useState } from 'react';
import type { Conversacion } from '../../api/ops/buzon';
import type { Cliente as FichaCliente, ResumenCliente } from '../../api/ops/clientes';
import type { Operaciones } from '../../api/operaciones';
import { Boton } from '../../componentes/Boton';
import { Campo } from '../../componentes/Campo';
import { Chip, type EstadoChip } from '../../componentes/Chip';
import { Icono } from '../../componentes/Icono';
import { PanelLateral } from '../../componentes/PanelLateral';
import { Ventana } from '../../componentes/Ventana';
import { dinero } from '../../logica/dinero';
import { CON_AVISO, canal, horaDeLista, nombreDe, textoAvisoPrivacidad } from './reglas';

/**
 * Ficha del contacto de la conversación (11.3, RV Panel): con cliente vinculado, su ficha de `getCustomer`
 * (`clientes.fichas.ver`): datos, visitas y los permisos de este canal, «Abrir en Clientes» y desvincular; sin vincular,
 * «Vincular a un cliente». Debajo, el aviso de privacidad del contacto (`privacy_notice_sent_at`, G191) en WhatsApp,
 * Instagram y Telegram. Sin el próximo compromiso ni las etiquetas: `Customer` no los trae con su nombre.
 */
const PERMISO: Readonly<Record<string, readonly [string, EstadoChip]>> = {
    granted: ['Concedido', 'confirmed'],
    none: ['Sin permiso', 'neutral'],
    pending_confirmation: ['Pendiente', 'pending'],
    withdrawn: ['Baja', 'problem'],
};

interface Props {
    abierto: boolean;
    conversacion: Conversacion;
    ops: Operaciones;
    negocio: string;
    movil: boolean;
    puedeVincular: boolean;
    zona?: string;
    onCerrar: () => void;
    onVincular: () => void;
    onDesvincular: (nombre: string) => void;
    onAbrir: (cliente: string) => void;
}

export function FichaDelContacto({ abierto, conversacion: c, ops, negocio, movil, puedeVincular, zona, onCerrar, onVincular, onDesvincular, onAbrir }: Props) {
    const cliente = c.contact.customer_id ?? null;
    const [ficha, setFicha] = useState<{ id: string; datos: FichaCliente | null } | null>(null);

    useEffect(() => {
        if (!abierto || cliente === null) {
            return;
        }
        let vivo = true;
        ops.getCustomer(negocio, cliente)
            .then((r) => { if (vivo) setFicha({ id: cliente, datos: r.datos }); })
            .catch(() => { if (vivo) setFicha({ id: cliente, datos: null }); });
        return () => { vivo = false; };
    }, [abierto, cliente, ops, negocio]);

    const [nombreCanal] = canal(c.channel_type);
    const datos = ficha !== null && ficha.id === cliente ? ficha.datos : null;
    const nombre = datos?.display_name ?? nombreDe(c);
    const vinculado = cliente !== null;
    const permisos = (datos?.consents ?? []).filter((p) => p.channel === c.channel_type);
    const lineas: [string, string][] = datos === null ? [] : [
        ['Teléfono', datos.account?.phone ?? datos.local.phone ?? '—'],
        ['Email', datos.account?.email ?? datos.local.email ?? '—'],
        ['Visitas', [String(datos.stats?.visits_count ?? 0), datos.stats?.total_spent === undefined ? '' : dinero(datos.stats.total_spent)].filter((t) => t !== '').join(' · ')],
        ['Última', horaDeLista(datos.stats?.last_visit_at ?? null, new Date(), zona) || '—'],
    ];

    return (
        <PanelLateral
            abierto={abierto}
            onCerrar={onCerrar}
            movil={movil}
            tamano="ficha"
            titulo={nombre}
            eyebrow={vinculado ? 'Cliente' : 'Sin vincular'}
            subtitulo={vinculado ? undefined : `${nombreCanal} · ${c.contact.display_identifier}`}
            icono="user"
            chip={vinculado && datos !== null ? (datos.account == null ? 'Local' : 'Vinculado') : undefined}
            chipEstado={datos?.account == null ? 'neutral' : 'confirmed'}
            accion={vinculado ? 'Abrir en Clientes' : ''}
            accionIcono="external-link"
            onAccion={() => { if (cliente !== null) onAbrir(cliente); }}
        >
            {vinculado ? (
                <>
                    {lineas.length > 0 && (
                        <div className="rv-bz-lineas">
                            {lineas.map(([k, v]) => <div key={k}><span>{k}</span><span>{v}</span></div>)}
                        </div>
                    )}
                    {datos !== null && (
                        <div className="rv-ca-bloque">
                            <h3 className="rv-bz-ceja">Permisos de este canal</h3>
                            <div className="rv-bz-lineas">
                                {permisos.length === 0 ? (
                                    <div><span>{nombreCanal}</span><Chip estado="neutral">Sin permiso</Chip></div>
                                ) : (
                                    permisos.map((p) => {
                                        const [texto, estado] = PERMISO[p.state] ?? [p.state, 'neutral'];
                                        return <div key={p.purpose}><span>{`${nombreCanal} · ${p.purpose === 'marketing' ? 'comercial' : 'servicio'}`}</span><Chip estado={estado}>{texto}</Chip></div>;
                                    })
                                )}
                            </div>
                        </div>
                    )}
                    {puedeVincular && <Boton tipo="secundario" icono="unlink" movil={movil} onClick={() => { onDesvincular(nombre); }}>Desvincular de esta conversación</Boton>}
                </>
            ) : (
                <>
                    <p className="rv-bz-texto">{`Esta conversación de ${nombreCanal} no está vinculada a ningún cliente de tu negocio. Si sabes quién es, vincúlala para ver su ficha aquí.`}</p>
                    {puedeVincular && <Boton tipo="secundario" icono="link" movil={movil} onClick={onVincular}>Vincular a un cliente</Boton>}
                </>
            )}
            {CON_AVISO.includes(c.channel_type) && (
                <div className="rv-ca-bloque">
                    <h3 className="rv-bz-ceja">Aviso de privacidad</h3>
                    <p className="rv-bz-nota"><Icono nombre="shield-check" /><span>{textoAvisoPrivacidad(c, zona)}</span></p>
                </div>
            )}
        </PanelLateral>
    );
}

/** «Vincular a un cliente»: solo clientes de este negocio (`listCustomers` con `q`). */
export function VentanaVincular({ ops, negocio, movil, onElegir, onCerrar }: { ops: Operaciones; negocio: string; movil: boolean; onElegir: (cliente: string, nombre: string) => void; onCerrar: () => void }) {
    const [q, setQ] = useState('');
    const [resultados, setResultados] = useState<{ q: string; lista: ResumenCliente[] } | null>(null);

    useEffect(() => {
        let vivo = true;
        const t = setTimeout(() => {
            ops.listCustomers(negocio, { q: q.trim() === '' ? undefined : q.trim(), limit: q.trim() === '' ? 4 : 6 })
                .then((l) => { if (vivo) setResultados({ q, lista: l.data }); })
                .catch(() => { if (vivo) setResultados({ q, lista: [] }); });
        }, 250);
        return () => {
            vivo = false;
            clearTimeout(t);
        };
    }, [ops, negocio, q]);

    return (
        <Ventana abierta titulo="Vincular a un cliente" texto="Solo clientes de tu negocio. La conversación aparecerá en su ficha." cancelar="Cancelar" movil={movil} onCerrar={onCerrar}>
            <Campo etiqueta="Nombre, teléfono o email" tipo="search" valor={q} onChange={setQ} nombre="buscar-cliente" />
            <div className="rv-bz-resultados">
                {resultados !== null && resultados.lista.length === 0 && q.trim() !== '' && <p className="rv-bz-texto">{`No hay clientes con «${q.trim()}» en tu negocio.`}</p>}
                {(resultados?.lista ?? []).map((cl) => (
                    <button key={cl.id} type="button" className="rv-bz-resultado" onClick={() => { onElegir(cl.id, cl.display_name); }}>
                        <strong>{cl.display_name}</strong>
                        <small>{[cl.phone, cl.email].filter((x): x is string => x != null && x !== '').join(' · ')}</small>
                    </button>
                ))}
            </div>
        </Ventana>
    );
}
